'use client';

import { useState, useEffect, useCallback } from 'react';
import { Icon } from './Icon';

export interface VBCSaveButtonProps {
  vendorId: string | number;
  /** Whether the user is currently authenticated. */
  isAuthenticated?: boolean;
  /** Called when an unauthenticated user clicks save. App opens sign-in. */
  onRequestAuth?: () => void;
  /** If the saved state is known at render time, pass it here to skip the mount check. */
  initialSaved?: boolean;
  /** Called on mount to check if vendor is saved. App queries its own API. */
  onCheckSaved?: (vendorId: string | number) => Promise<boolean>;
  /** Called when user toggles save. Returns the new saved state. App handles persistence. */
  onToggleSave?: (vendorId: string | number, currentlySaved: boolean) => Promise<boolean>;
}

/**
 * Save/unsave vendor button for the VBC detail page.
 * Auth and persistence are delegated to the app via callback props.
 */
export function VBCSaveButton({
  vendorId,
  isAuthenticated,
  onRequestAuth,
  initialSaved,
  onCheckSaved,
  onToggleSave,
}: VBCSaveButtonProps) {
  const [saved, setSaved] = useState(initialSaved ?? false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(initialSaved === undefined && !!onCheckSaved);

  // Check saved state on mount if no initialSaved was provided
  useEffect(() => {
    if (initialSaved !== undefined || !onCheckSaved || !isAuthenticated) {
      setChecking(false);
      return;
    }

    let cancelled = false;
    onCheckSaved(vendorId)
      .then((isSaved) => {
        if (!cancelled) setSaved(isSaved);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setChecking(false);
      });

    return () => {
      cancelled = true;
    };
  }, [vendorId, isAuthenticated, initialSaved, onCheckSaved]);

  const handleToggle = useCallback(async () => {
    if (!isAuthenticated) {
      onRequestAuth?.();
      return;
    }

    if (!onToggleSave) return;

    setLoading(true);
    try {
      const newSaved = await onToggleSave(vendorId, saved);
      setSaved(newSaved);
    } catch {
      // Silent fail
    } finally {
      setLoading(false);
    }
  }, [vendorId, isAuthenticated, saved, onRequestAuth, onToggleSave]);

  if (checking) {
    return (
      <button
        disabled
        className="inline-flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium opacity-60"
        style={{
          borderColor: 'var(--brand-card-border)',
          color: 'var(--brand-text-muted)',
        }}
      >
        <Icon name="progress_activity" size={16} className="animate-spin" />
        Save
      </button>
    );
  }

  return (
    <button
      onClick={handleToggle}
      disabled={loading}
      className="inline-flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-all duration-150 disabled:opacity-60"
      style={
        saved
          ? {
              borderColor: 'var(--brand-primary, #014F8A)',
              backgroundColor: 'var(--brand-primary, #014F8A)',
              color: 'white',
            }
          : {
              borderColor: 'var(--brand-card-border)',
              color: 'var(--brand-text-muted)',
              backgroundColor: 'white',
            }
      }
      aria-label={saved ? 'Remove from saved vendors' : 'Save vendor'}
    >
      {loading ? (
        <Icon name="progress_activity" size={16} className="animate-spin" />
      ) : saved ? (
        <Icon name="bookmark_added" size={16} />
      ) : (
        <Icon name="bookmark" size={16} />
      )}
      {saved ? 'Saved' : 'Save'}
    </button>
  );
}
